import { DELETE_MESSAGE_FOR_EVERYONE, DELETE_MESSAGE_FOR_ME } from '../Actions/Constants';
import logger from '../Helpers/logger';
import dbConnectionManager from './DbConnectionManager';

const MESSAGES = 'messages';

class MessagesIdb {
    constructor() {
        if (!MessagesIdb.instance) {
            MessagesIdb.instance = this;
        }
        return MessagesIdb.instance;
    }

    async getConnection() {
        return dbConnectionManager.getConnection();
    }

    async saveMessages(messages) {
        try {
            const db = await this.getConnection();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([MESSAGES], 'readwrite');
                const objectStore = transaction.objectStore(MESSAGES);

                messages.forEach(msg => {
                    if (msg && msg.msgId) {
                        objectStore.put(msg);
                    } else {
                        logger.warn('[IDB] Skipping message with no msgId:', msg);
                    }
                });

                transaction.oncomplete = () => {
                    resolve();
                };

                transaction.onerror = event => {
                    logger.error('[IDB] error saveMessages', event.target.error);
                    reject(event.target.error);
                };
            });
        } catch (error) {
            logger.error('[IDB] error saveMessages', error);
        }
    }

    async getMessageById(msgId) {
        try {
            if (!msgId) return null;
            const db = await this.getConnection();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([MESSAGES], 'readonly');
                const objectStore = transaction.objectStore(MESSAGES);
                const request = objectStore.get(msgId);

                request.onsuccess = event => {
                    resolve(event.target.result || null);
                };

                request.onerror = event => {
                    logger.error('[IDB] error getMessageById', event.target.error);
                    reject(event.target.error);
                };
            });
        } catch (error) {
            logger.error('[IDB] error getMessageById', error);
            return null;
        }
    }

    async getMessagesByUserJid(userJid, limit = 100) {
        try {
            const db = await this.getConnection();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([MESSAGES], 'readonly');
                const objectStore = transaction.objectStore(MESSAGES);
                const index = objectStore.index('userJid_timestamp');
                const range = IDBKeyRange.bound([userJid, -Infinity], [userJid, Infinity]);
                const request = index.openCursor(range, 'prev');
                const messages = [];

                request.onsuccess = event => {
                    const cursor = event.target.result;
                    if (cursor && messages.length < limit) {
                        messages.push(cursor.value);
                        cursor.continue();
                    } else {
                        resolve(messages);
                    }
                };

                request.onerror = event => {
                    logger.error('[IDB] error getMessagesByUserJid', event.target.error);
                    reject(event.target.error);
                };
            });
        } catch (error) {
            logger.error('[IDB] error getMessagesByUserJid', error);
            return [];
        }
    }

    async getAllJids() {
        try {
            const db = await this.getConnection();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([MESSAGES], 'readonly');
                const objectStore = transaction.objectStore(MESSAGES);
                const index = objectStore.index('userJid_timestamp');
                const jids = new Set();
                const request = index.openKeyCursor();

                request.onsuccess = event => {
                    const cursor = event.target.result;
                    if (cursor) {
                        jids.add(cursor.key[0]);
                        cursor.continue();
                    } else {
                        resolve(Array.from(jids));
                    }
                };

                request.onerror = event => {
                    reject(event.target.error);
                };
            });
        } catch (error) {
            logger.error('[IDB] error getAllJids', error);
            return [];
        }
    }

    async deleteMessagesByUserJid(userJid) {
        try {
            const db = await this.getConnection();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([MESSAGES], 'readwrite');
                const objectStore = transaction.objectStore(MESSAGES);
                const index = objectStore.index('userJid_timestamp');
                const range = IDBKeyRange.bound([userJid, -Infinity], [userJid, Infinity]);
                const request = index.openCursor(range);

                request.onsuccess = event => {
                    const cursor = event.target.result;
                    if (cursor) {
                        cursor.delete();
                        cursor.continue();
                    } else {
                        resolve();
                    }
                };

                transaction.oncomplete = () => {
                    resolve();
                };

                transaction.onerror = event => {
                    logger.error('[IDB] error deleteMessagesByUserJid', event.target.error);
                    reject(event.target.error);
                };
            });
        } catch (error) {
            logger.error('[IDB] error deleteMessagesByUserJid', error);
        }
    }

    async clearMessages(userJid) {
        try {
            const db = await this.getConnection();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([MESSAGES], 'readwrite');
                const objectStore = transaction.objectStore(MESSAGES);
                const index = objectStore.index('userJid_timestamp');
                const range = IDBKeyRange.bound([userJid, -Infinity], [userJid, Infinity]);
                const request = index.openCursor(range);

                request.onsuccess = event => {
                    const cursor = event.target.result;
                    if (cursor) {
                        cursor.delete();
                        cursor.continue();
                    } else {
                        resolve();
                    }
                };

                transaction.oncomplete = () => {
                    resolve();
                };

                transaction.onerror = event => {
                    logger.error('[IDB] error clearMessages', event.target.error);
                    reject(event.target.error);
                };
            });
        } catch (error) {
            logger.error('[IDB] error clearMessages', error);
        }
    }

    async updateMessage(message) {
        try {
            if (!message || !message.msgId) return;
            const db = await this.getConnection();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([MESSAGES], 'readwrite');
                const objectStore = transaction.objectStore(MESSAGES);
                const request = objectStore.put(message);

                request.onsuccess = () => {
                    resolve();
                };

                request.onerror = event => {
                    logger.error('[IDB] error updateMessage', event.target.error);
                    reject(event.target.error);
                };
            });
        } catch (error) {
            logger.error('[IDB] error updateMessage', error);
        }
    }

    /**
     * Upsert (Update or Insert) a message
     * If the message exists, merge the new data with existing data
     * If the message doesn't exist, create it with the provided data
     * @param {Object} message - Message object with at least msgId, userJid, and timestamp
     */
    async upsertMessage(message) {
        try {
            if (!message || !message.msgId) {
                logger.warn('[MessagesIdb] upsertMessage requires msgId');
                return;
            }

            const existing = await this.getMessageById(message.msgId);

            if (existing) {
                // Message exists - merge and update
                const merged = { ...existing, ...message };
                return this.updateMessage(merged);
            } else {
                // Message doesn't exist - create new
                // Ensure we have the required fields for indexing
                if (!message.userJid || !message.timestamp) {
                    logger.warn(`[MessagesIdb] Cannot create message ${message.msgId}: missing userJid or timestamp`);
                    return;
                }

                // Prevent creating a "ghost" message from a status update for an already-deleted message
                if (!message.msgBody) {
                    logger.warn(`[MessagesIdb] Ignoring status update for deleted message ${message.msgId}`);
                    return;
                }

                return this.updateMessage(message);
            }
        } catch (error) {
            logger.error('[IDB] error upsertMessage', error);
        }
    }

    async updateDeletedMessagesInIdb(actionType, data) {
        try {
            const db = await this.getConnection();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([MESSAGES], 'readwrite');
                const objectStore = transaction.objectStore(MESSAGES);

                if (actionType === DELETE_MESSAGE_FOR_ME) {
                    const msgIds = Array.isArray(data.msgIds) ? data.msgIds : Object.values(data.msgIds || {});
                    msgIds.forEach(msgId => {
                        objectStore.delete(msgId);
                    });
                } else if (actionType === DELETE_MESSAGE_FOR_EVERYONE) {
                    const messageIds = data.msgId.split(',');
                    messageIds.forEach(async msgId => {
                        const getRequest = objectStore.get(msgId);
                        getRequest.onsuccess = event => {
                            const message = event.target.result;
                            if (message) {
                                message.deleteStatus = 2;
                                objectStore.put(message);
                            }
                        };
                    });
                }

                transaction.oncomplete = () => {
                    resolve();
                };

                transaction.onerror = event => {
                    logger.error('[IDB] error updateDeletedMessagesInIdb', event.target.error);
                    reject(event.target.error);
                };
            });
        } catch (error) {
            logger.error('[IDB] error updateDeletedMessagesInIdb', error);
        }
    }
}

const messagesIdb = new MessagesIdb();

if (typeof window !== 'undefined') {
    window.inspectDB = async (userJid, limit = 1000) => {
        if (!userJid) {
            const jids = await messagesIdb.getAllJids();
            logger.debug('[MessagesIdb] No JID provided. Available JIDs in DB:', jids);
            return jids;
        }

        const messages = await messagesIdb.getMessagesByUserJid(userJid, limit);
        logger.debug(`[MessagesIdb] Found ${messages.length} messages for ${userJid}`);

        if (messages.length > 0) {
            logger.debug(messages);
        } else {
            const allJids = await messagesIdb.getAllJids();
            const matching = allJids.filter(j => j.includes(userJid.split('@')[0]));
            if (matching.length > 0) {
                logger.debug(`[MessagesIdb] No exact match for "${userJid}", but found these similar JIDs:`, matching);
            } else {
                logger.debug('[MessagesIdb] No messages found and no similar JIDs found. Available JIDs:', allJids);
            }
        }
        return messages;
    };

    window.inspectDBById = async msgId => {
        const message = await messagesIdb.getMessageById(msgId);
        logger.debug(`[MessagesIdb] Found message for ${msgId}:`, message);
        return message;
    };
}

export default messagesIdb;
